import { useQuery, useQueryClient } from '@tanstack/react-query';
import { rendicionCampanaService } from '../../../services/api';
import { LABEL_ETAPA, LABEL_TIPO_ELECCION } from './types';
import type { TipoEleccion, EtapaEleccion } from './types';

interface LineaRubro {
  codigo: string;
  denominacion: string;
  importe: number;
  esAutoCalculado: boolean;
}

interface AporteResumen {
  apellido: string;
  nombre: string | null;
  razonSocial: string | null;
  dni: string | null;
  cuit: string;
  tipoPersona: 'HUMANA' | 'JURIDICA';
  modalidad: 'TRANSFERENCIA' | 'DEPOSITO' | 'ESPECIE';
  descripcionEspecie: string | null;
  fecha: string;
  importe: number;
}

interface ReporteCampanaData {
  rendicionId: string;
  partido: {
    id: string;
    numero: number;
    denominacion: string;
    sigla: string | null;
    domicilioLegal: string | null;
    localidad: string | null;
  };
  acto: {
    tipoEleccion: TipoEleccion;
    etapa: EtapaEleccion;
    fechaActoElectoral: string;
    plazoVencimiento: string | null;
  };
  candidaturas: Array<{ categoria: string; detalle: string | null; orden: number }>;
  ingresos: { lineas: LineaRubro[]; total: number };
  egresos: { lineas: LineaRubro[]; total: number };
  resultado: number;
  aportes: AporteResumen[];
  totalesAportes: {
    humanasDinero: number;
    humanasEspecie: number;
    juridicasDinero: number;
    juridicasEspecie: number;
    total: number;
    cantidad: number;
  };
  generadoEn: string;
}

interface Props {
  rendicionId: string;
  onBack: () => void;
  onNext: () => void;
}

function formatImporte(n: number | string | null | undefined): string {
  return Number(n ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(f: string): string {
  return new Date(f).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function Paso5VistaPrevia({ rendicionId, onBack, onNext }: Props) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['rendicion-campana-reporte', rendicionId],
    queryFn: async () => {
      const res = await rendicionCampanaService.getReportes(rendicionId);
      return res.data as ReporteCampanaData;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });

  function refrescar() {
    queryClient.invalidateQueries({ queryKey: ['rendicion-campana-reporte', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-campana-movimientos', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-campana-aportes', rendicionId] });
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-sm text-gray-500">
        Generando vista previa...
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-sm text-red-600 mb-3">No se pudo generar la vista previa.</p>
        <button
          type="button"
          onClick={refrescar}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const sigla = data.partido.sigla ? ` (${data.partido.sigla})` : '';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end -mb-2">
        <button
          type="button"
          onClick={refrescar}
          title="Actualizar paso"
          className="text-xs text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
        >
          🔄 Actualizar
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3">
          Vista previa del informe
        </h2>

        <div className="text-center text-xs text-gray-500 mt-2">
          {data.partido.denominacion}{sigla} · CHUBUT
        </div>
        <h3 className="text-center text-base font-bold uppercase text-gray-900 underline tracking-wide">
          Informe de Ingresos y Egresos de Campaña Electoral
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm pt-2">
          <div>
            <span className="font-semibold">Elección:</span> {LABEL_TIPO_ELECCION[data.acto.tipoEleccion]}
          </div>
          <div>
            <span className="font-semibold">Etapa:</span> {LABEL_ETAPA[data.acto.etapa]}
          </div>
          <div>
            <span className="font-semibold">Fecha del acto:</span> {formatFecha(data.acto.fechaActoElectoral)}
          </div>
          {data.acto.plazoVencimiento && (
            <div>
              <span className="font-semibold">Plazo presentación:</span>{' '}
              <span className="text-amber-700">{formatFecha(data.acto.plazoVencimiento)}</span>
            </div>
          )}
        </div>

        {data.candidaturas.length > 0 && (
          <div className="pt-2">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Categorías declaradas</h4>
            <ul className="text-sm text-gray-700 space-y-0.5">
              {data.candidaturas.map((c, i) => (
                <li key={i}>
                  • <strong>{c.categoria}</strong>
                  {c.detalle && <span className="text-gray-600"> — {c.detalle}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Ingresos */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-3">
          Ingresos
        </h3>
        <BloqueTabla lineas={data.ingresos.lineas} total={data.ingresos.total} totalLabel="TOTAL INGRESOS" />
      </div>

      {/* Egresos */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 mb-3">
          Egresos
        </h3>
        <BloqueTabla lineas={data.egresos.lineas} total={data.egresos.total} totalLabel="TOTAL EGRESOS" />
      </div>

      {/* Resultado */}
      <div className="bg-white rounded-lg shadow p-6 text-right">
        <div className="text-xs text-gray-600 uppercase font-semibold">
          Resultado de campaña ({data.resultado >= 0 ? 'Superávit' : 'Déficit'})
        </div>
        <div
          className={`text-3xl font-bold font-mono mt-1 ${
            data.resultado >= 0 ? 'text-green-700' : 'text-red-700'
          }`}
        >
          $ {formatImporte(Math.abs(data.resultado))}
        </div>
      </div>

      {/* Aportantes */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-3 border-b border-gray-200 pb-2">
          <h3 className="text-base font-semibold text-gray-900">
            Listado de aportantes <span className="text-gray-500 text-sm">({data.totalesAportes.cantidad})</span>
          </h3>
          <div className="text-xs text-gray-500">
            Total aportes: <span className="font-mono font-semibold text-gray-800">$ {formatImporte(data.totalesAportes.total)}</span>
          </div>
        </div>

        {data.aportes.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No hay aportes cargados.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Aportante</th>
                  <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">DNI / CUIT</th>
                  <th className="px-2 py-1.5 text-left font-medium text-gray-500 uppercase">Modalidad</th>
                  <th className="px-2 py-1.5 text-right font-medium text-gray-500 uppercase">Importe</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {data.aportes.map((a, i) => (
                  <tr key={i}>
                    <td className="px-2 py-1.5 text-gray-700">{formatFecha(a.fecha)}</td>
                    <td className="px-2 py-1.5 text-gray-900">
                      {a.tipoPersona === 'HUMANA'
                        ? `${a.apellido}, ${a.nombre ?? ''}`
                        : a.razonSocial ?? a.apellido}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-gray-700">
                      {a.dni && <span>DNI {a.dni} · </span>}
                      {a.cuit}
                    </td>
                    <td className="px-2 py-1.5 text-gray-700">
                      {a.modalidad === 'ESPECIE'
                        ? `Especie${a.descripcionEspecie ? ` — ${a.descripcionEspecie}` : ''}`
                        : a.modalidad}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-gray-900">
                      {formatImporte(a.importe)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <TotalMini label="Humanas dinero" value={data.totalesAportes.humanasDinero} />
          <TotalMini label="Humanas especie" value={data.totalesAportes.humanasEspecie} />
          <TotalMini label="Jurídicas dinero" value={data.totalesAportes.juridicasDinero} />
          <TotalMini label="Jurídicas especie" value={data.totalesAportes.juridicasEspecie} />
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600 italic">
        Este informe tendrá carácter de declaración jurada al firmarse (Art. 53 inc. c, Ley XII-23). Se
        firma por el contador certificante y las autoridades partidarias.
      </div>

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg"
        >
          ← Anterior
        </button>
        <button
          type="button"
          onClick={onNext}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-5 py-2 rounded-lg"
        >
          Continuar a firma →
        </button>
      </div>
    </div>
  );
}

function BloqueTabla({
  lineas,
  total,
  totalLabel,
}: {
  lineas: LineaRubro[];
  total: number;
  totalLabel: string;
}) {
  if (lineas.length === 0) {
    return <p className="text-sm text-gray-500 italic">Sin movimientos cargados.</p>;
  }
  return (
    <table className="min-w-full divide-y divide-gray-200 text-sm">
      <tbody className="divide-y divide-gray-100">
        {lineas.map((l) => (
          <tr key={l.codigo}>
            <td className="px-2 py-1.5 font-mono text-xs text-gray-500 w-24">{l.codigo}</td>
            <td className="px-2 py-1.5 text-gray-700">
              {l.denominacion}
              {l.esAutoCalculado && (
                <span className="ml-2 text-[10px] text-purple-700">(auto)</span>
              )}
            </td>
            <td className="px-2 py-1.5 text-right font-mono text-gray-900 w-44">
              $ {formatImporte(l.importe)}
            </td>
          </tr>
        ))}
        <tr className="bg-gray-50 border-t-2 border-gray-300">
          <td colSpan={2} className="px-2 py-2 text-right font-semibold text-gray-900">
            {totalLabel}
          </td>
          <td className="px-2 py-2 text-right font-mono font-bold text-gray-900">
            $ {formatImporte(total)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function TotalMini({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-2 text-center">
      <div className="text-[10px] uppercase font-semibold text-gray-600">{label}</div>
      <div className="font-mono font-semibold text-gray-900">$ {formatImporte(value)}</div>
    </div>
  );
}
